import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle, Clock, RotateCcw } from "lucide-react";

type Status = {
  locked: boolean;
  owned_by_me: boolean;
  same_session: boolean;
  expires_at: string | null;
  seconds_remaining: number;
};

type Props = {
  studioId: string | null;
  start: Date | null;
  end: Date | null;
  sessionToken?: string | null;
  /** Called when user clicks Retry — typically re-runs your submit/select handler. */
  onRetry?: () => void;
  /** Show admin debug info (session_token, slot key, reuse). */
  showDebug?: boolean;
  /** Compact = single-line variant for inline placement (e.g. time grid). */
  compact?: boolean;
};

/**
 * Polls the slot lock status for a given studio/time range and shows a friendly
 * notice with a countdown + retry button if the slot is held by someone else.
 * If the slot is held by the current user/session it shows a reassuring "your hold"
 * banner instead. Designed to be reusable across the Booking page, Availability page,
 * and admin new-booking dialog.
 */
export function SlotLockNotice({ studioId, start, end, sessionToken, onRetry, showDebug, compact }: Props) {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<Status | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!studioId || !start || !end) { setStatus(null); return; }
    let alive = true;
    const fetchStatus = async () => {
      const { data } = await supabase.rpc("get_slot_lock_status" as any, {
        _studio_id: studioId,
        _start: start.toISOString(),
        _end: end.toISOString(),
        _session_token: sessionToken ?? null,
      });
      if (!alive) return;
      const row = Array.isArray(data) ? data[0] : data;
      setStatus(row ?? null);
    };
    fetchStatus();
    const id = window.setInterval(fetchStatus, 5000);
    return () => { alive = false; window.clearInterval(id); };
  }, [studioId, start?.toISOString(), end?.toISOString(), sessionToken]);

  useEffect(() => {
    if (!status?.locked) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [status?.locked]);

  if (!studioId || !start || !end) return null;

  const seconds = status?.expires_at
    ? Math.max(0, Math.floor((new Date(status.expires_at).getTime() - now) / 1000))
    : 0;
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  const showAdminDebug = showDebug && isAdmin;
  const isMine = !!status?.owned_by_me || !!status?.same_session;

  if (!status?.locked) {
    if (!showAdminDebug) return null;
    return (
      <div className="border border-border bg-panel p-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
        <div className="text-primary mb-1">Slot lock debug</div>
        <div>Studio: {studioId.slice(0, 8)}…</div>
        <div>Slot: {start.toISOString()} → {end.toISOString()}</div>
        <div>Session token: {sessionToken ?? "(not yet generated)"}</div>
        <div>Lock status: no active hold</div>
      </div>
    );
  }

  if (isMine) {
    return (
      <div className={`border border-primary/40 bg-primary/10 text-primary ${compact ? "px-3 py-2" : "p-4"} flex items-center gap-3`}>
        <Clock className="w-4 h-4 shrink-0" />
        <div className="flex-1">
          <div className="font-mono text-xs uppercase tracking-widest">Your hold · {mm}:{ss} left</div>
          {!compact && (
            <div className="text-xs opacity-80 mt-1">This time is reserved for you while you finish checkout.</div>
          )}
        </div>
        {showAdminDebug && (
          <div className="text-[10px] font-mono opacity-70">
            <div>reuse={String(status.same_session)}</div>
            <div>mine={String(status.owned_by_me)}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`border border-amber-500/40 bg-amber-500/10 text-amber-200 ${compact ? "px-3 py-2" : "p-4"}`}>
      <div className="flex items-start gap-3">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-mono text-xs uppercase tracking-widest">Slot temporarily held</div>
          {!compact && (
            <p className="text-xs opacity-90 mt-1">
              Someone else started checkout for this exact time. The hold expires in{" "}
              <span className="font-mono text-amber-100">{mm}:{ss}</span> — try again then, or pick another time.
            </p>
          )}
          {onRetry && (
            <Button
              variant="outline" size="sm"
              className="mt-3 border-amber-400/50 text-amber-100 hover:bg-amber-500/20"
              onClick={onRetry} disabled={seconds > 0}
            >
              <RotateCcw className="w-3 h-3 mr-2" />
              {seconds > 0 ? `Retry in ${mm}:${ss}` : "Retry now"}
            </Button>
          )}
        </div>
        {showAdminDebug && (
          <div className="text-[10px] font-mono opacity-70 leading-tight">
            <div>expires={status.expires_at}</div>
            <div>mine={String(status.owned_by_me)}</div>
            <div>reuse={String(status.same_session)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SlotLockNotice;